import { Controller } from '@hotwired/stimulus';

// Liste déroulante avec recherche sur un <select> natif (simple ou multiple),
// qui reste la valeur envoyée avec le formulaire : recherche sans casse ni
// accents, options en listbox ARIA, nombre de résultats annoncé (status),
// choix multiples en étiquettes retirables ; liste ouverte au clic, à la
// frappe ou par la flèche bas (Alt+flèche bas : sans surbrillance), jamais
// au seul focus ; clavier : flèches, Entrée, Échap, Retour arrière. Le
// required du select (sans JavaScript) devient l'aria-required de la
// saisie : le select masqué ne bloque pas l'envoi. Chaque choix émet
// `change` sur le <select> ; clear() (ou l'événement combobox:clear) vide
// sans l'émettre.
const normalize = (text) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

export default class extends Controller {
    static targets = ['select', 'widget', 'input', 'listbox', 'tags', 'status'];
    static values = { placeholder: String };

    connect() {
        this.selectTarget.hidden = true;
        this.selectTarget.required = false;
        this.widgetTarget.hidden = false;
        this.query = '';
        this.active = -1;
        this.render();
    }

    get multiple() {
        return this.selectTarget.multiple;
    }

    // Options proposées : celles qui ont un libellé (pas l'option vide de tête).
    get choices() {
        return [...this.selectTarget.options].filter((option) => option.text.trim() !== '');
    }

    get items() {
        return [...this.listboxTarget.querySelectorAll('[role="option"]')];
    }

    open() {
        if (this.inputTarget.disabled || !this.listboxTarget.hidden) return;
        this.query = '';
        if (!this.multiple) this.inputTarget.select();
        this.renderList();
        this.listboxTarget.hidden = false;
        this.inputTarget.setAttribute('aria-expanded', 'true');
    }

    close() {
        this.listboxTarget.hidden = true;
        this.inputTarget.setAttribute('aria-expanded', 'false');
        this.resetActive();
        this.announce('');
    }

    // Aucune option en surbrillance (la liste vient d'être reconstruite).
    resetActive() {
        this.active = -1;
        this.inputTarget.removeAttribute('aria-activedescendant');
    }

    blur() {
        this.close();
        this.render();
    }

    search() {
        this.query = this.inputTarget.value;
        this.renderList();
        this.listboxTarget.hidden = false;
        this.inputTarget.setAttribute('aria-expanded', 'true');
    }

    key(event) {
        switch (event.key) {
        case 'ArrowDown':
            event.preventDefault();
            this.open();
            if (!event.altKey) this.move(1);
            break;
        case 'ArrowUp':
            event.preventDefault();
            this.open();
            this.move(-1);
            break;
        case 'Enter':
            if (this.listboxTarget.hidden) return;
            event.preventDefault(); // liste ouverte : Entrée choisit, n'envoie pas le formulaire
            if (this.active < 0) return;
            this.choose(this.items[this.active].dataset.value);
            break;
        case 'Escape':
            if (this.listboxTarget.hidden) return;
            event.preventDefault();
            event.stopPropagation();
            this.close();
            break;
        case 'Backspace':
            if (this.multiple && this.inputTarget.value === '') {
                const last = this.choices.filter((option) => option.selected).at(-1);
                if (last) this.toggle(last, false);
            }
            break;
        default:
        }
    }

    // Clic dans la liste ou sur une étiquette : le champ garde le focus.
    keep(event) {
        event.preventDefault();
    }

    pick(event) {
        this.choose(event.currentTarget.dataset.value);
    }

    remove(event) {
        const option = this.choices.find((choice) => choice.value === event.currentTarget.dataset.value);
        if (option) this.toggle(option, false);
    }

    clear() {
        if (this.multiple) {
            [...this.selectTarget.options].forEach((option) => { option.selected = false; });
        } else {
            this.selectTarget.value = '';
        }
        this.query = '';
        this.render();
    }

    choose(value) {
        const option = this.choices.find((choice) => choice.value === value);
        if (!option) return;
        if (this.multiple) {
            this.inputTarget.value = '';
            this.query = '';
            this.toggle(option, !option.selected);
            this.renderList();
        } else {
            option.selected = true;
            this.close();
            this.render();
            this.changed();
        }
    }

    toggle(option, selected) {
        option.selected = selected;
        this.render();
        this.changed();
    }

    changed() {
        this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }

    move(delta) {
        const { items } = this;
        if (items.length === 0) return;
        this.active = Math.min(Math.max(this.active + delta, 0), items.length - 1);
        items.forEach((item, index) => { item.toggleAttribute('data-active', index === this.active); });
        this.inputTarget.setAttribute('aria-activedescendant', items[this.active].id);
        items[this.active].scrollIntoView({ block: 'nearest' });
    }

    // Champ et étiquettes d'après le <select>.
    render() {
        const selected = this.choices.filter((option) => option.selected);
        if (this.multiple) {
            this.tagsTarget.replaceChildren(...selected.map((option) => this.tag(option)));
            this.inputTarget.placeholder = selected.length ? '' : this.placeholderValue;
        } else {
            const option = selected.find((choice) => choice.value !== '');
            this.inputTarget.value = option ? option.text : '';
            this.inputTarget.placeholder = this.placeholderValue;
        }
    }

    tag(option) {
        const chip = document.createElement('span');
        chip.className = 'inline-flex items-center gap-1 rounded bg-accent-soft px-1.5 py-0.5 text-xs text-accent-strong';
        chip.textContent = option.text;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = '-my-1 -mr-1 inline-grid size-6 place-items-center rounded leading-none hover:bg-accent/10 hover:text-ink';
        button.textContent = '×';
        button.dataset.value = option.value;
        // Le champ garde le focus : sa perte reconstruirait les étiquettes avant le clic.
        button.dataset.action = 'mousedown->combobox#keep combobox#remove';
        button.setAttribute('aria-label', `Retirer ${option.text}`);
        chip.append(button);
        return chip;
    }

    renderList() {
        const query = normalize(this.query);
        const matches = this.choices.filter((option) => normalize(option.text).includes(query));
        const items = matches.map((option, index) => {
            const item = document.createElement('li');
            item.id = `${this.listboxTarget.id}-${index}`;
            item.setAttribute('role', 'option');
            item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
            item.dataset.value = option.value;
            item.dataset.action = 'mousedown->combobox#keep click->combobox#pick';
            item.className = 'cursor-pointer px-3 py-1.5 hover:bg-surface data-active:bg-accent-soft aria-selected:font-semibold';
            item.textContent = option.text;
            return item;
        });
        if (items.length === 0) {
            const empty = document.createElement('li');
            empty.className = 'px-3 py-1.5 text-muted';
            empty.textContent = 'Aucun résultat';
            items.push(empty);
        }
        this.listboxTarget.replaceChildren(...items);
        this.resetActive();
        this.announce(matches.length === 0 ? 'Aucun résultat' : `${matches.length} résultat${matches.length > 1 ? 's' : ''}`);
    }

    // Nombre de résultats lu par les lecteurs d'écran (status poli).
    announce(text) {
        if (this.hasStatusTarget && this.statusTarget.textContent !== text) this.statusTarget.textContent = text;
    }
}
